import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { UnderwritingFormField } from '../../../../app/adapters/types';
import type { UnderwritingQuestions } from './questions';

/** What step 7's sections and questions read from the step, around the engine that renders them. */
export interface QuestionsState {
  readonly form: UnderwritingQuestions;
  /** Each question's field as the server sent it, by id: its products, which the engine's copy may not carry. */
  readonly fields: ReadonlyMap<string, UnderwritingFormField>;
  /** The market's currency, as USD, for the sign before an amount: absent until the market is read. */
  readonly currency?: string;
  /**
   * Whether a required question with no answer is marked: only where something tried to answer for the broker, the
   * documents they uploaded or the client's Risk Pool record, so what it couldn't answer is what is marked. A broker who
   * fills in the form by hand isn't shown every question as missing.
   */
  readonly marksUnanswered?: boolean;
  /** Whether a top-level section, by its place among the sections, is on the page shown: off it, it is kept but hidden. */
  readonly onPage?: (section: number) => boolean;
}

const QuestionsContext = createContext<QuestionsState | null>(null);

export const QuestionsProvider = QuestionsContext.Provider;

export function useQuestions(): QuestionsState {
  const state = useContext(QuestionsContext);
  if (!state) {
    throw new Error('Step 7 questions are drawn inside a QuestionsProvider');
  }
  return state;
}

/** Whether a question sits in a grouped question's list, where it is drawn as a row. */
export const GroupContext = createContext(false);

/**
 * The products that ask every question in a grouped question's list, which its stem says once: the rows beside it
 * don't repeat them.
 */
export const SharedProductsContext = createContext<readonly string[]>([]);

/**
 * Opens the folded section a question sits in. A question the checks refuse opens it, though every question in it has
 * an answer: the broker is taken to that question, and a folded section would hide it.
 */
export const RevealSectionContext = createContext<() => void>(() => undefined);

/** The answers read from the documents that the broker has said look right, by field id, for this visit. */
export interface ConfirmedAnswers {
  readonly confirmed: ReadonlySet<string>;
  readonly confirm: (fieldId: string) => void;
}

const ConfirmedAnswersContext = createContext<ConfirmedAnswers | null>(null);

/**
 * Holds which answers the broker has confirmed on this visit. Each save sends them, and fir-account keeps a confirmed
 * answer saved unchanged as the broker's own, at full confidence, so it reads as theirs when the step opens again.
 */
export function ConfirmedAnswersProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const [confirmed, setConfirmed] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const confirm = useCallback(
    (fieldId: string) =>
      setConfirmed((before) => new Set([...Array.from(before), fieldId])),
    []
  );
  const value = useMemo(() => ({ confirmed, confirm }), [confirmed, confirm]);
  return (
    <ConfirmedAnswersContext.Provider value={value}>
      {children}
    </ConfirmedAnswersContext.Provider>
  );
}

export function useConfirmedAnswers(): ConfirmedAnswers {
  const state = useContext(ConfirmedAnswersContext);
  if (!state) {
    throw new Error(
      'Step 7 questions are drawn inside a ConfirmedAnswersProvider'
    );
  }
  return state;
}
